/**
 * TaskColumn Component
 *
 * A virtualized column for the Kanban board displaying tasks of a specific status.
 * Uses @tanstack/react-virtual for performance with many tasks.
 *
 * @module components/tasks/TaskColumn
 */

import { memo, useRef, useCallback } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Plus } from 'lucide-react';
import TaskCard from './TaskCard.jsx';
import { TASK_STATUS_CONFIG } from './TaskStatusBadge.jsx';

// ============================================
// Constants
// ============================================

/**
 * Estimated height of a task card in pixels
 */
const ESTIMATED_CARD_HEIGHT = 120;

/**
 * Number of items to render outside the visible area
 */
const OVERSCAN_COUNT = 5;

/**
 * Gap between cards in pixels
 */
const CARD_GAP = 8;

// ============================================
// Component
// ============================================

/**
 * TaskColumn Component
 *
 * @param {object} props - Component props
 * @param {string} props.status - Column status (matches TaskStatus enum)
 * @param {Array} props.tasks - Tasks to display in this column
 * @param {function} [props.onTaskClick] - Handler when a task is clicked
 * @param {function} [props.onAddTask] - Handler when add task button is clicked
 * @param {boolean} [props.showAddButton=true] - Whether to show the add task button
 * @param {boolean} [props.isDropTarget=false] - Whether this column is a drop target
 * @param {Set} [props.highlightedTaskIds] - Set of task IDs to highlight (for live updates)
 * @param {string} [props.className=''] - Additional CSS classes
 * @returns {React.ReactElement} Column element
 */
const TaskColumn = memo(function TaskColumn({
  status,
  tasks = [],
  onTaskClick,
  onAddTask,
  showAddButton = true,
  isDropTarget = false,
  highlightedTaskIds = new Set(),
  className = ''
}) {
  const parentRef = useRef(null);
  const statusConfig = TASK_STATUS_CONFIG[status];

  // Setup droppable area for dnd-kit
  const { setNodeRef, isOver } = useDroppable({
    id: status
  });

  // Setup virtualizer for performance
  const virtualizer = useVirtualizer({
    count: tasks.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ESTIMATED_CARD_HEIGHT + CARD_GAP,
    overscan: OVERSCAN_COUNT
  });

  const handleAddClick = useCallback(() => {
    if (onAddTask) {
      onAddTask(status);
    }
  }, [onAddTask, status]);

  if (!statusConfig) {
    console.warn(`Unknown status: ${status}`);
    return null;
  }

  const { label: statusLabel, Icon: StatusIcon, text: textColor } = statusConfig;

  return (
    <div
      ref={setNodeRef}
      className={`
        flex flex-col h-full min-h-0
        bg-gray-900/50 rounded-lg border border-gray-700
        transition-all duration-200 ease-out
        ${isOver ? 'border-indigo-500 bg-indigo-500/10 scale-[1.02] shadow-lg shadow-indigo-500/20' : ''}
        ${className}
      `}
    >
      {/* Column Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-700">
        <div className="flex items-center gap-2">
          <StatusIcon className={`w-4 h-4 ${textColor}`} />
          <h3 className="text-sm font-medium text-gray-200">
            {statusLabel}
          </h3>
          <span className="text-xs text-gray-500 bg-gray-800 px-2 py-0.5 rounded-full">
            {tasks.length}
          </span>
        </div>
        {showAddButton && onAddTask && (
          <button
            onClick={handleAddClick}
            className="p-1 text-gray-500 hover:text-gray-300 hover:bg-gray-700 rounded transition-colors"
            title={`Add task to ${statusLabel}`}
          >
            <Plus className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Scrollable Task List */}
      <div
        ref={parentRef}
        className="flex-1 overflow-y-auto p-2 min-h-0"
      >
        <SortableContext
          items={tasks.map(t => t.id)}
          strategy={verticalListSortingStrategy}
        >
          {tasks.length === 0 ? (
            <div className="flex items-center justify-center h-24 text-gray-600 text-sm">
              No tasks
            </div>
          ) : (
            <div
              style={{
                height: virtualizer.getTotalSize(),
                width: '100%',
                position: 'relative'
              }}
            >
              {virtualizer.getVirtualItems().map((virtualRow) => {
                const task = tasks[virtualRow.index];
                return (
                  <TaskCard
                    key={task.id}
                    task={task}
                    onClick={onTaskClick}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: virtualRow.size - CARD_GAP,
                      transform: `translateY(${virtualRow.start}px)`
                    }}
                    isHighlighted={highlightedTaskIds.has(task.id)}
                  />
                );
              })}
            </div>
          )}
        </SortableContext>
      </div>
    </div>
  );
});

/**
 * Non-virtualized version for smaller lists
 * Use this when you have fewer than 20 tasks in a column
 */
export const TaskColumnSimple = memo(function TaskColumnSimple({
  status,
  tasks = [],
  onTaskClick,
  onAddTask,
  showAddButton = true,
  highlightedTaskIds = new Set(),
  className = ''
}) {
  const statusConfig = TASK_STATUS_CONFIG[status];

  const { setNodeRef, isOver } = useDroppable({
    id: status
  });

  const handleAddClick = useCallback(() => {
    if (onAddTask) {
      onAddTask(status);
    }
  }, [onAddTask, status]);

  if (!statusConfig) {
    return null;
  }

  const { label: statusLabel, Icon: StatusIcon, text: textColor } = statusConfig;

  return (
    <div
      ref={setNodeRef}
      className={`
        flex flex-col h-full min-h-0
        bg-gray-900/50 rounded-lg border border-gray-700
        transition-all duration-200 ease-out
        ${isOver ? 'border-indigo-500 bg-indigo-500/10 scale-[1.02] shadow-lg shadow-indigo-500/20' : ''}
        ${className}
      `}
    >
      {/* Column Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-700">
        <div className="flex items-center gap-2">
          <StatusIcon className={`w-4 h-4 ${textColor}`} />
          <h3 className="text-sm font-medium text-gray-200">
            {statusLabel}
          </h3>
          <span className="text-xs text-gray-500 bg-gray-800 px-2 py-0.5 rounded-full">
            {tasks.length}
          </span>
        </div>
        {showAddButton && onAddTask && (
          <button
            onClick={handleAddClick}
            className="p-1 text-gray-500 hover:text-gray-300 hover:bg-gray-700 rounded transition-colors"
            title={`Add task to ${statusLabel}`}
          >
            <Plus className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Task List (non-virtualized) */}
      <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-0">
        <SortableContext
          items={tasks.map(t => t.id)}
          strategy={verticalListSortingStrategy}
        >
          {tasks.length === 0 ? (
            <div className="flex items-center justify-center h-24 text-gray-600 text-sm">
              No tasks
            </div>
          ) : (
            tasks.map(task => (
              <TaskCard
                key={task.id}
                task={task}
                onClick={onTaskClick}
                isHighlighted={highlightedTaskIds.has(task.id)}
              />
            ))
          )}
        </SortableContext>
      </div>
    </div>
  );
});

export default TaskColumn;
